import { useState, useEffect, useCallback } from 'react';
import {
  AttachMoney as MoneyIcon,
  ShoppingCart as CartIcon,
  Receipt as ReceiptIcon,
  AssignmentReturn as DevolucionesIcon,
  TrendingUp as MarginIcon,
  Inventory as StockIcon,
  BarChart as TopProductsIcon,
  ShowChart as ProductTrendIcon,
} from '@mui/icons-material';
import { DashboardService } from '../../../api/services/DashboardService';
import type { DashboardKpiItemDto } from '../../../api/models/DashboardKpiItemDto';
import type { KPIConfig } from '../../../types/kpi.types';
import type { ChartType, StatisticsFilterState } from '../../../types/statistics.types';
import {
  CHARTS_WITH_COMPARISON,
  CHARTS_WITH_PARTIAL_COMPARISON,
} from '../../../types/statistics.types';
import {
  getMonthRange,
  getYearRange,
  getPeriodKey,
  getPeriodLabel,
  normalizeToDayOfMonth,
  aggregateDailyToMonthly,
  type PeriodData,
} from './statisticsComparisonUtils';
import { chartPalette } from '../../dashboard/chartPalette';

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const dateRangeToApiParams = (range: StatisticsFilterState['dateRange']) => ({
  from: startOfDay(range.startDate).toISOString(),
  to: endOfDay(range.endDate).toISOString(),
});

/** Orden igual al main page: sales, units, ticket, orders, porCobrar, devoluciones, intercambios.
 *  Statistics solo incluye los que tienen gráfica: sales, units, orders, devoluciones + margin, top-products, stock-ingress. */
const buildStatisticsKpis = (): KPIConfig[] => [
  {
    id: 'sales',
    title: 'Ventas',
    value: 0,
    icon: <MoneyIcon />,
    variant: 'primary',
    format: 'currency',
  },
  {
    id: 'units',
    title: 'Piezas',
    value: 0,
    icon: <CartIcon />,
    variant: 'success',
    format: 'number',
  },
  {
    id: 'orders',
    title: 'Número de ventas',
    value: 0,
    icon: <ReceiptIcon />,
    variant: 'violet',
    format: 'number',
  },
  {
    id: 'devoluciones',
    title: 'Devoluciones',
    value: 0,
    icon: <DevolucionesIcon />,
    variant: 'slate',
    format: 'breakdown',
    breakdown: [
      { label: 'Devuelto', value: 0, orderCount: 0, color: chartPalette.status.error },
      { label: 'Retenido', value: 0, orderCount: 0, color: chartPalette.status.success },
    ],
  },
  {
    id: 'margin',
    title: 'Margen',
    value: 0,
    icon: <MarginIcon />,
    variant: 'info',
    format: 'currency',
  },
  {
    id: 'top-products',
    title: 'Top productos',
    value: 0,
    icon: <TopProductsIcon />,
    variant: 'info',
    format: 'number',
  },
  {
    id: 'product-trend',
    title: 'Tendencia por producto',
    value: 0,
    icon: <ProductTrendIcon />,
    variant: 'info',
    format: 'number',
  },
  {
    id: 'stock-ingress',
    title: 'Stock ingresado',
    value: 0,
    icon: <StockIcon />,
    variant: 'primary',
    format: 'number',
  },
];

const mapApiKpisToStatistics = (
  items: DashboardKpiItemDto[] | undefined,
  fallback: KPIConfig[],
): KPIConfig[] => {
  const byId = new Map((items ?? []).map((item) => [item.id, item]));
  return fallback.map((base) => {
    const source = byId.get(base.id);
    if (source == null) return base;
    return {
      ...base,
      value: source.value,
      format:
        (source as { format?: string }).format === 'breakdown'
          ? 'breakdown'
          : (source as { format?: string }).format === 'currency'
            ? 'currency'
            : 'number',
      ...((
        source as {
          breakdown?: { label: string; value: number; orderCount?: number; color?: string }[];
        }
      ).breakdown != null
        ? {
            breakdown: (
              source as {
                breakdown?: { label: string; value: number; orderCount?: number; color?: string }[];
              }
            ).breakdown,
          }
        : {}),
    };
  });
};

export interface ComparisonMeta {
  mode: 'months' | 'years';
  periodLabels: string[];
  periodKeys: string[];
  /** true para top-products (comparación parcial) */
  isPartial?: boolean;
}

export interface UseStatisticsDataResult {
  kpis: KPIConfig[];
  chartData: unknown;
  loading: boolean;
  refetch: () => void;
  /** Presente cuando chartData es resultado de comparación */
  comparisonMeta?: ComparisonMeta;
}

/** Obtiene el valor numérico a graficar según el tipo de gráfica (para comparación) */
function getValueForChart(
  chartType: ChartType,
  point: { date: string; [key: string]: unknown },
): number {
  const p = point as Record<string, unknown>;
  switch (chartType) {
    case 'sales':
      return (p.total as number) ?? 0;
    case 'orders':
      return (p.orders as number) ?? 0;
    case 'units':
      return (p.units as number) ?? 0;
    case 'margin':
      return (p.margin as number) ?? 0;
    case 'devoluciones':
      return ((p.devuelto as number) ?? 0) + ((p.retenido as number) ?? 0);
    case 'stock-ingress':
      return (p.totalUnits as number) ?? 0;
    default:
      return 0;
  }
}

export function useStatisticsData(
  filters: StatisticsFilterState,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isGlobalScope: boolean,
): UseStatisticsDataResult {
  const [kpis, setKpis] = useState<KPIConfig[]>(() => buildStatisticsKpis());
  const [chartData, setChartData] = useState<unknown>([]);
  const [loading, setLoading] = useState(true);
  const [comparisonMeta, setComparisonMeta] = useState<ComparisonMeta | undefined>(undefined);

  const isComparisonMode =
    (filters.comparisonMode === 'months' || filters.comparisonMode === 'years') &&
    filters.basePeriod != null &&
    Array.isArray(filters.comparePeriods) &&
    filters.comparePeriods.length > 0 &&
    CHARTS_WITH_COMPARISON.includes(filters.chartType);

  const isPartialComparisonMode =
    (filters.comparisonMode === 'months' || filters.comparisonMode === 'years') &&
    filters.basePeriod != null &&
    Array.isArray(filters.comparePeriods) &&
    filters.comparePeriods.length > 0 &&
    CHARTS_WITH_PARTIAL_COMPARISON.includes(filters.chartType);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setComparisonMeta(undefined);
    setChartData([]);
    const scopeParam = filters.scope === 'global' ? 'global' : String(filters.scope);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';

    try {
      if (isComparisonMode && filters.basePeriod && filters.comparePeriods) {
        const mode = filters.comparisonMode as 'months' | 'years';
        const allPeriods = [filters.basePeriod, ...filters.comparePeriods].slice(0, 4);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const getRange = mode === 'months' ? getMonthRange : getYearRange;

        const periodLabels = allPeriods.map((p) => getPeriodLabel(p, mode));
        const periodKeys = allPeriods.map((p) => getPeriodKey(p));

        const fetchPromises = allPeriods.map((period) => {
          const { from, to } =
            mode === 'months' && period.month != null
              ? getMonthRange(period.year, period.month)
              : getYearRange(period.year);
          const asOfDate = to;
          return fetchChartData(filters.chartType, scopeParam, from, to, asOfDate, tz);
        });

        const kpiRange =
          mode === 'months' && filters.basePeriod.month != null
            ? getMonthRange(filters.basePeriod.year, filters.basePeriod.month)
            : getYearRange(filters.basePeriod.year);
        const [kpisRes, ...chartResults] = await Promise.all([
          DashboardService.dashboardControllerGetKpis(scopeParam, kpiRange.from, kpiRange.to, tz),
          ...fetchPromises,
        ]);

        const periodData: PeriodData<{ date: string; [key: string]: unknown }>[] = chartResults.map(
          (data, i) => ({
            periodKey: periodKeys[i],
            periodLabel: periodLabels[i],
            data: Array.isArray(data) ? (data as { date: string; [key: string]: unknown }[]) : [],
          }),
        );

        const getValue = (point: { date: string; [key: string]: unknown }) =>
          getValueForChart(filters.chartType, point);

        const merged =
          mode === 'months'
            ? normalizeToDayOfMonth(periodData, getValue)
            : aggregateDailyToMonthly(periodData, getValue);

        setKpis(mapApiKpisToStatistics(kpisRes?.kpis, buildStatisticsKpis()));
        setChartData(merged);
        setComparisonMeta({ mode, periodLabels, periodKeys });
      } else if (
        isPartialComparisonMode &&
        filters.basePeriod &&
        filters.comparePeriods &&
        filters.chartType === 'top-products'
      ) {
        const mode = filters.comparisonMode as 'months' | 'years';
        const allPeriods = [filters.basePeriod, ...filters.comparePeriods].slice(0, 4);
        const periodLabels = allPeriods.map((p) => getPeriodLabel(p, mode));
        const periodKeys = allPeriods.map((p) => getPeriodKey(p));

        const fetchPromises = allPeriods.map((period) => {
          const { from, to } =
            mode === 'months' && period.month != null
              ? getMonthRange(period.year, period.month)
              : getYearRange(period.year);
          return DashboardService.dashboardControllerGetTopProducts(scopeParam, from, to, tz);
        });

        const kpiRange =
          mode === 'months' && filters.basePeriod.month != null
            ? getMonthRange(filters.basePeriod.year, filters.basePeriod.month)
            : getYearRange(filters.basePeriod.year);
        const [kpisRes, ...topResults] = await Promise.all([
          DashboardService.dashboardControllerGetKpis(scopeParam, kpiRange.from, kpiRange.to, tz),
          ...fetchPromises,
        ]);

        const baseTop = (topResults[0] ?? []) as {
          productId: number;
          productName: string;
          count: number;
          revenue: number;
        }[];
        const byPeriod = topResults.map(
          (arr) =>
            new Map(
              (arr ?? []).map((p: { productId: number; count: number; revenue: number }) => [
                p.productId,
                p,
              ]),
            ),
        );

        const merged = baseTop.map((p) => {
          const row: Record<string, unknown> = {
            productId: p.productId,
            productName: p.productName,
            displayName:
              p.productName.length > 35 ? p.productName.slice(0, 32) + '...' : p.productName,
          };
          periodKeys.forEach((key, i) => {
            const found = byPeriod[i]?.get(p.productId);
            row[key] = found?.count ?? 0;
          });
          return row;
        });

        setKpis(mapApiKpisToStatistics(kpisRes?.kpis, buildStatisticsKpis()));
        setChartData(merged);
        setComparisonMeta({ mode, periodLabels, periodKeys, isPartial: true });
      } else {
        const { from, to } = dateRangeToApiParams(filters.dateRange);
        const asOfDate = endOfDay(filters.dateRange.endDate).toISOString();

        const productIds =
          filters.chartType === 'product-trend' ? (filters.selectedProductIds ?? []) : undefined;

        const [kpisRes, chartRes] = await Promise.all([
          DashboardService.dashboardControllerGetKpis(scopeParam, from, to, tz),
          fetchChartData(filters.chartType, scopeParam, from, to, asOfDate, tz, productIds),
        ]);

        setKpis(mapApiKpisToStatistics(kpisRes?.kpis, buildStatisticsKpis()));
        setChartData(chartRes ?? []);
      }
    } catch {
      setKpis(buildStatisticsKpis());
      setChartData([]);
      setComparisonMeta(undefined);
    } finally {
      setLoading(false);
    }
  }, [
    filters.chartType,
    filters.dateRange,
    filters.scope,
    filters.selectedProductIds,
    filters.comparisonMode,
    filters.basePeriod,
    filters.comparePeriods,
    isComparisonMode,
    isPartialComparisonMode,
  ]);

  useEffect(() => {
    let cancelled = false;
    void fetchData().then(() => {
      if (!cancelled) {
        // fetchData already updates state
      }
    });
    return () => {
      cancelled = true;
    };
  }, [fetchData]);

  const refetch = useCallback(() => {
    void fetchData();
  }, [fetchData]);

  return { kpis, chartData, loading, refetch, comparisonMeta };
}

async function fetchChartData(
  chartType: ChartType,
  scope: string,
  from: string,
  to: string,
  asOfDate: string,
  tz: string,
  productIds?: number[],
): Promise<unknown> {
  switch (chartType) {
    case 'sales':
    case 'orders':
      return DashboardService.dashboardControllerGetSalesMoneyTrend(scope, from, to, tz, 'day');
    case 'units':
      return DashboardService.dashboardControllerGetSalesUnitsTrend(scope, from, to, tz, 'day');
    case 'margin':
      return DashboardService.dashboardControllerGetMarginTrend(scope, from, to, tz, 'day');
    case 'devoluciones':
      return DashboardService.dashboardControllerGetDevolucionesTrend(scope, from, to, tz, 'day');
    case 'top-products':
      return DashboardService.dashboardControllerGetTopProducts(scope, from, to, tz);
    case 'product-trend':
      if (productIds && productIds.length >= 1) {
        return DashboardService.dashboardControllerGetProductSalesTrend(
          productIds,
          scope,
          from,
          to,
          tz,
          'day',
        );
      }
      return [];
    case 'stock-ingress':
      return DashboardService.dashboardControllerGetStockIngressTrend(scope, from, to, tz, 'day');
    case 'stock-by-location':
      return DashboardService.dashboardControllerGetStockByLocation(scope, asOfDate);
    case 'inventory-status':
      return DashboardService.dashboardControllerGetInventoryStatus(scope, asOfDate);
    case 'inventory-transfers':
      return DashboardService.dashboardControllerGetInventoryTransfersStatus(scope, from, to, tz);
    case 'inventory-records':
      return DashboardService.dashboardControllerGetInventoryRecordsStatus(scope, from, to, tz);
    case 'orders-status':
      return DashboardService.dashboardControllerGetOrdersStatus(scope, from, to, tz);
    default:
      return [];
  }
}
